/**
 * Picking a type's scenarios, then starting a bulk run, building a dataset from them, or adding
 * them to a dataset as cases. A selection belongs to the type it was made in and drops when
 * another opens. Starting hands over to the bulk-run or dataset page, which is where the work is
 * watched.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';
import { toast } from '@/components/ui/sonner';
import { bulkRunUrl, datasetUrl } from '@/constants/paths';
import { getApiErrorMessage } from '@/lib/utils';
import { aiFunctionService } from '@/services/ai-function.service';
import { simulationDatasetService } from '@/services/simulation-dataset.service';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import type { RunMode } from '@/types/websocket.types';
import { BULK_RUN_MAX_ITEMS, type Scenario } from '../types';

/**
 * `typeScenarios` are the ones that can be picked, and `max` how many: a dataset being added to leaves out
 * its own cases and takes only what its ceiling leaves room for.
 */
export const useScenarioSelection = (
	sopId: string,
	typeId: string | null,
	typeScenarios: Scenario[],
	max: number = BULK_RUN_MAX_ITEMS
) => {
	const navigate = useNavigate();
	const buId = useAtomValue(selectedBusinessUnitIdAtom);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [mode, setMode] = useState<RunMode>('simulated');
	const [starting, setStarting] = useState(false);
	const [creatingDataset, setCreatingDataset] = useState(false);
	const [addingCases, setAddingCases] = useState(false);
	const scenarioIds = useMemo(() => typeScenarios.map((s) => s.id), [typeScenarios]);

	// What was picked belongs to the type being left, not the one being opened.
	useEffect(() => setSelectedIds(new Set()), [typeId]);

	const toggle = useCallback((scenario: Scenario) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(scenario.id)) next.delete(scenario.id);
			else if (next.size < max) next.add(scenario.id);
			return next;
		});
	}, [max]);

	/** Out of the selection as well as the list, for a scenario that no longer exists. */
	const drop = useCallback((scenarioId: string) => {
		setSelectedIds((prev) => {
			if (!prev.has(scenarioId)) return prev;
			const next = new Set(prev);
			next.delete(scenarioId);
			return next;
		});
	}, []);

	const selectFirst = useCallback(() => {
		setSelectedIds(new Set(scenarioIds.slice(0, max)));
	}, [scenarioIds, max]);

	/**
	 * Back from a bulk run to change what it ran: tick the server's record of that run, filtered
	 * against the type on screen and capped like `toggle`.
	 */
	const restoreRun = useCallback(
		async (runId: string) => {
			if (!typeId) return;
			const run = await aiFunctionService.fetchBulkRun(sopId, typeId, runId);
			if (!run) return;
			const present = new Set(scenarioIds);
			const ids = run.items.map((item) => item.scenarioId).filter((id) => present.has(id));
			setSelectedIds(new Set(ids.slice(0, max)));
		},
		[sopId, typeId, scenarioIds, max]
	);

	const clear = useCallback(() => setSelectedIds(new Set()), []);

	const run = useCallback(async () => {
		if (!typeId || selectedIds.size === 0) return;
		setStarting(true);
		try {
			await aiFunctionService.startBulkRun(sopId, typeId, mode, [...selectedIds], buId);
			navigate(bulkRunUrl(sopId, typeId));
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Could not start the run'));
		} finally {
			setStarting(false);
		}
	}, [sopId, typeId, selectedIds, mode, buId, navigate]);

	const createDataset = useCallback(
		async (name: string, description: string | null) => {
			if (!typeId || selectedIds.size === 0) return;
			setCreatingDataset(true);
			try {
				const { dataset } = await simulationDatasetService.create(
					{ name, description, sopId, scenarioTypeId: typeId, scenarioIds: [...selectedIds] },
					buId
				);
				navigate(datasetUrl(dataset.id));
			} catch (err) {
				toast.error(getApiErrorMessage(err, 'Could not create the dataset'));
			} finally {
				setCreatingDataset(false);
			}
		},
		[sopId, typeId, selectedIds, buId, navigate]
	);

	const addCases = useCallback(
		async (datasetId: string) => {
			if (selectedIds.size === 0) return;
			setAddingCases(true);
			try {
				await simulationDatasetService.addCases(datasetId, [...selectedIds]);
				navigate(datasetUrl(datasetId));
			} catch (err) {
				toast.error(getApiErrorMessage(err, 'Could not add the cases'));
			} finally {
				setAddingCases(false);
			}
		},
		[selectedIds, navigate]
	);

	return {
		selectedIds,
		mode,
		setMode,
		starting,
		toggle,
		drop,
		selectFirst,
		restoreRun,
		clear,
		run,
		creatingDataset,
		createDataset,
		addingCases,
		addCases
	};
};
